/**
 * Private Voice Chat Window
 * Floating window for moderator private voice chat with individual players.
 *
 * It stays live while the player's card it was started from stays open: a
 * FloatingWindow (components/floating-window.tsx), the narrow one, over every
 * dialog's scrim and not modal — it takes neither the focus nor Escape from
 * the card, as the game client's call window beside a dialog: the moderator
 * mutes and ends the call with a click.
 */

import { useCallback, useEffect, useState, Component, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { LiveKitRoom, RoomAudioRenderer, useRemoteParticipants, useLocalParticipant } from '@livekit/components-react';
import { usePrivateVoiceChatStore } from './stores/private-voice-chat';
import { roomFailure, microphoneFailure } from './room-failure';
import voiceChat from './private-voice-chat-window.module.css';
import { FloatingWindow } from '../../components/floating-window';
import { WorldAndTeam } from './world-and-team';
import { RoomStatus } from './room-status';
import { useRemoteVolumes } from './remote-volumes';
import { isPlayerIdentity } from './room-identities';
import { showToast } from '../../components/toast/toast-store';
import i18n from '../../i18n/config';

interface PrivateVoiceChatWindowProps {
  onClose: () => void;
}

/**
 * Error Boundary for LiveKit errors. It stands around the call's room alone: a
 * render error there takes the room down and leaves the window — its words
 * and its close — so the moderator can still end the call, which tells the
 * player's match it is over.
 */
class PrivateVoiceChatErrorBoundary extends Component<
  { children: ReactNode; onError: (error: Error) => void },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; onError: (error: Error) => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('[PrivateVoiceChatErrorBoundary] Caught error:', error);
    this.props.onError(error);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

/**
 * Private Voice Chat Controllers
 * Manages participants state for private chat
 */
function PrivateVoiceChatControllers() {
  const remoteParticipants = useRemoteParticipants();
  const setPlayerSpeaking = usePrivateVoiceChatStore(state => state.setPlayerSpeaking);
  const isPlayerMutedLocally = usePrivateVoiceChatStore(state => state.isPlayerMutedLocally);

  // Track player speaking state
  useEffect(() => {
    const playerParticipant = remoteParticipants.find((p) => isPlayerIdentity(p.identity));
    
    if (playerParticipant) {
      setPlayerSpeaking(playerParticipant.isSpeaking);
    } else {
      setPlayerSpeaking(false);
    }

    // Poll for speaking updates
    const interval = setInterval(() => {
      const playerParticipant = remoteParticipants.find((p) => isPlayerIdentity(p.identity));
      if (playerParticipant) {
        setPlayerSpeaking(playerParticipant.isSpeaking);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [remoteParticipants, setPlayerSpeaking]);

  // The player heard or muted on this browser, as the moderator chose: the
  // player alone, whoever else is in the room.
  const mutedFor = useCallback(
    (identity: string) => (isPlayerIdentity(identity) ? isPlayerMutedLocally : undefined),
    [isPlayerMutedLocally],
  );
  useRemoteVolumes(mutedFor);

  return null;
}

/**
 * What a LiveKit error says, in the moderator's words (room-failure.ts);
 * LiveKit's own message, in English, goes to the log alone. The microphone's
 * is a warning the call goes on through, as the game client's call gives it;
 * a connection cancelled as the window closed says nothing.
 */
function sayRoomError(error: Error) {
  if (microphoneFailure(error)) {
    showToast('error', i18n.t('privateVoiceChat.microphone.title'), i18n.t('privateVoiceChat.microphone.message'));
    return;
  }
  const failure = roomFailure(error);
  if (failure) usePrivateVoiceChatStore.getState().roomFailed(failure);
}

/**
 * The room's onError, one for the window's whole life. LiveKitRoom connects
 * again whenever the onError it is given changes, and the window rendered on
 * every change to the call's store — a failure among them — so one written in
 * the render tried a failed call again on each: about twenty times in the
 * second after it failed, and on, slowed only by the browser, while the
 * window stayed open.
 */
function onRoomError(error: Error) {
  console.error('[PrivateVoiceChatWindow] LiveKit error:', error);
  sayRoomError(error);
}

/**
 * Private Voice Chat Window Component
 * Displays floating window with moderator and player
 */
export function PrivateVoiceChatWindow({ onClose }: PrivateVoiceChatWindowProps) {
  const { t } = useTranslation();
  // What the window shows, and the actions it hands on: it renders again only
  // when one of them changes. Read whole, the store drew it again at every
  // write — the player's speaking every 100ms, each step of a drag.
  const {
    connectionState,
    serverUrl,
    moderatorToken,
    roomName,
    failure,
    cohortName,
    worldName,
    playerName,
    isPlayerSpeaking,
    isPlayerMutedLocally,
    roomConnected,
    roomDisconnected,
    setWindowPosition,
    togglePlayerMute,
  } = usePrivateVoiceChatStore(
    useShallow((s) => ({
      connectionState: s.connectionState,
      serverUrl: s.serverUrl,
      moderatorToken: s.moderatorToken,
      roomName: s.roomName,
      failure: s.failure,
      cohortName: s.cohortName,
      worldName: s.worldName,
      playerName: s.playerName,
      isPlayerSpeaking: s.isPlayerSpeaking,
      isPlayerMutedLocally: s.isPlayerMutedLocally,
      roomConnected: s.roomConnected,
      roomDisconnected: s.roomDisconnected,
      setWindowPosition: s.setWindowPosition,
      togglePlayerMute: s.togglePlayerMute,
    })),
  );
  // Where it was left, read once as it opens: the window keeps its own place from then on.
  const [storedPosition] = useState(() => usePrivateVoiceChatStore.getState().windowPosition);

  if (!serverUrl || !moderatorToken || !roomName) {
    return null;
  }

  return (
    <FloatingWindow
      title={t('privateVoiceChat.title')}
      closeLabel={t('common.close')}
      moveLabel={t('common.moveWindow')}
      onClose={onClose}
      // Draggable by its header, remembering where it was left.
      initialPosition={storedPosition}
      onPositionChange={setWindowPosition}
    >
      {(cohortName || worldName) && <WorldAndTeam world={worldName || '-'} team={cohortName || '-'} size="narrow" />}

      <RoomStatus connectionState={connectionState} failure={failure} size="narrow" />

      <PrivateVoiceChatErrorBoundary onError={sayRoomError}>
        <LiveKitRoom
          token={moderatorToken}
          serverUrl={serverUrl}
          connect={true}
          audio={true}
          video={false}
          onConnected={roomConnected}
          onDisconnected={roomDisconnected}
          onError={onRoomError}
        >
          <PrivateVoiceChatControllers />
          <RoomAudioRenderer />

          {/* A call that failed has no one in it: no microphone to mute, and
              no player to wait for — the status above says why. */}
          {connectionState !== 'error' && (
            <PrivateVoiceChatParticipantsList
              playerName={playerName}
              isPlayerSpeaking={isPlayerSpeaking}
              isPlayerMutedLocally={isPlayerMutedLocally}
              togglePlayerMute={togglePlayerMute}
            />
          )}
        </LiveKitRoom>
      </PrivateVoiceChatErrorBoundary>
    </FloatingWindow>
  );
}

/**
 * Private Voice Chat Participants List
 * Shows moderator (self) and player
 */
interface PrivateVoiceChatParticipantsListProps {
  playerName: string | null;
  isPlayerSpeaking: boolean;
  isPlayerMutedLocally: boolean;
  togglePlayerMute: () => void;
}

function PrivateVoiceChatParticipantsList({
  playerName,
  isPlayerSpeaking,
  isPlayerMutedLocally,
  togglePlayerMute,
}: PrivateVoiceChatParticipantsListProps) {
  const { t } = useTranslation();
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const [isLocalMuted, setIsLocalMuted] = useState(false);

  const toggleLocalMute = async () => {
    if (localParticipant) {
      const newMuteState = !isLocalMuted;
      await localParticipant.setMicrophoneEnabled(!newMuteState);
      setIsLocalMuted(newMuteState);
    }
  };

  // Check if player is actually connected
  const playerParticipant = remoteParticipants.find((p) => isPlayerIdentity(p.identity));
  const isPlayerConnected = !!playerParticipant;

  return (
    <div className={voiceChat.voiceChatParticipants}>
      <h4>{t('privateVoiceChat.participants')}</h4>
      <ul className={voiceChat.voiceChatParticipantList}>
        {/* Moderator (You) */}
        <li
          className={`${voiceChat.voiceChatParticipant} ${
            localParticipant?.isSpeaking && !isLocalMuted ? voiceChat.voiceChatParticipantSpeaking : ''
          }`}
        >
          <div className={voiceChat.voiceChatParticipantAvatar}>
            {t('privateVoiceChat.moderatorInitial')}
            {localParticipant?.isSpeaking && !isLocalMuted && (
              <div className={voiceChat.voiceChatSpeakingRing} />
            )}
          </div>
          <span className={voiceChat.voiceChatParticipantName}>
            {t('privateVoiceChat.you')}
          </span>
          <button
            onClick={toggleLocalMute}
            className={`${voiceChat.voiceChatMuteBtn} ${
              isLocalMuted ? voiceChat.voiceChatMuteBtnMuted : ''
            }`}
            aria-label={
              isLocalMuted
                ? t('privateVoiceChat.unmute')
                : t('privateVoiceChat.mute')
            }
          >
            {isLocalMuted ? '🔇' : '🎤'}
          </button>
        </li>

        {/* Player - only show if connected */}
        {isPlayerConnected ? (
          <li
            className={`${voiceChat.voiceChatParticipant} ${
              isPlayerSpeaking ? voiceChat.voiceChatParticipantSpeaking : ''
            }`}
          >
            <div className={voiceChat.voiceChatParticipantAvatar}>
              {playerName?.charAt(0).toUpperCase() || 'P'}
              {isPlayerSpeaking && (
                <div className={voiceChat.voiceChatSpeakingRing} />
              )}
            </div>
            <span className={voiceChat.voiceChatParticipantName}>
              {playerName || t('privateVoiceChat.player')}
            </span>
            <button
              onClick={togglePlayerMute}
              className={`${voiceChat.voiceChatMuteBtn} ${
                isPlayerMutedLocally ? voiceChat.voiceChatMuteBtnMuted : ''
              }`}
              aria-label={
                isPlayerMutedLocally
                  ? t('privateVoiceChat.unmute')
                  : t('privateVoiceChat.mute')
              }
            >
              {isPlayerMutedLocally ? '🔇' : '🎧'}
            </button>
          </li>
        ) : (
          /* Player not connected yet - show waiting state */
          <li className={`${voiceChat.voiceChatParticipant} ${voiceChat.voiceChatParticipantWaiting}`}>
            <div className={`${voiceChat.voiceChatParticipantAvatar} ${voiceChat.voiceChatParticipantAvatarWaiting}`}>
              {playerName?.charAt(0).toUpperCase() || 'P'}
            </div>
            <span className={voiceChat.voiceChatParticipantName}>
              {playerName || t('privateVoiceChat.player')}
              <span className={voiceChat.voiceChatParticipantStatus}> - {t('privateVoiceChat.waitingPlayer')}</span>
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}
